import type {
	FastshiftCsProfilerCustomerProfile,
	FastshiftCsProfilerCustomerProfileTheme,
	FastshiftCsProfilerInsight,
	FastshiftCsProfilerLinkedTickets,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

export const EXPORT_CONFLUENCE_SITE = 'https://hello.atlassian.net';
export const EXPORT_CONFLUENCE_SPACE_KEY = 'CASP';

type LinkedTicketsByTheme = Record<string, FastshiftCsProfilerLinkedTickets | null | undefined>;

const themeTierSortRank = {
	HIGH: 0,
	BASE: 1,
	WATCH: 2,
} satisfies Record<FastshiftCsProfilerCustomerProfileTheme['tier'], number>;

const htmlEscapes: Record<string, string> = {
	'&': '&amp;',
	'<': '&lt;',
	'>': '&gt;',
	'"': '&quot;',
	"'": '&#39;',
};

const escapeHtml = (value: string | number) =>
	String(value).replace(/[&<>"']/g, (character) => htmlEscapes[character] ?? character);

const formatPercentage = (value: number) => Math.round(value * 100);

const formatExportDateTime = (date: Date) =>
	date.toLocaleString('en-AU', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});

const formatTicketValue = (value: string | number | null | undefined) =>
	value === null || value === undefined || value === '' ? '-' : escapeHtml(value);

const getThemeName = (theme: FastshiftCsProfilerCustomerProfileTheme) => theme.themeDetails.name;

const sortThemesBySeverity = (themes: readonly FastshiftCsProfilerCustomerProfileTheme[]) =>
	themes.slice().sort((firstTheme, secondTheme) => {
		return themeTierSortRank[firstTheme.tier] - themeTierSortRank[secondTheme.tier];
	});

const renderInsightByDimension: Record<
	FastshiftCsProfilerInsight['dimension'],
	(profile: FastshiftCsProfilerCustomerProfile, insight: FastshiftCsProfilerInsight) => string
> = {
	REGION_INDUSTRY: (profile, insight) =>
		`Customers within <strong>${escapeHtml(
			profile.selection.region,
		)}</strong> region and <strong>${escapeHtml(
			profile.selection.industry,
		)}</strong> industry face this problem <strong>${formatPercentage(insight.cellRate)}%</strong> of the time`,
	INDUSTRY: (profile, insight) =>
		`Customers in the <strong>${escapeHtml(
			profile.selection.industry,
		)}</strong> industry face this problem <strong>${formatPercentage(insight.cellRate)}%</strong> of the time`,
	REGION_SEGMENT: (profile, insight) =>
		`<strong>${escapeHtml(profile.selection.segment)}</strong> customers in <strong>${escapeHtml(
			profile.selection.region,
		)}</strong> face this problem <strong>${formatPercentage(insight.cellRate)}%</strong> of the time.`,
	SEGMENT: (profile, insight) =>
		`<strong>${escapeHtml(profile.selection.segment)}</strong> customers face this problem <strong>${formatPercentage(
			insight.cellRate,
		)}%</strong> of the time.`,
};

const renderInsights = (
	profile: FastshiftCsProfilerCustomerProfile,
	theme: FastshiftCsProfilerCustomerProfileTheme,
) => {
	if (theme.insights.length > 0) {
		const insightItems = theme.insights.map((insight) =>
			renderInsightByDimension[insight.dimension](profile, insight),
		);

		return `<h3>Insights</h3><ul>${insightItems.map((insight) => `<li>${insight}</li>`).join('')}</ul>`;
	}

	const fallbackInsight =
		theme.tier === 'BASE'
			? `All migrating customers face this problem <strong>${formatPercentage(
					theme.baseRate,
				)}%</strong> of the time, regardless of profile.`
			: escapeHtml(theme.themeDetails.description ?? 'No insights are available for this theme.');

	return `<h3>Insights</h3><ul><li>${fallbackInsight}</li></ul>`;
};

const renderEntTicketsTable = (linkedTickets: FastshiftCsProfilerLinkedTickets) => {
	if (linkedTickets.entTickets.length === 0) {
		return '';
	}

	const rows = linkedTickets.entTickets
		.map(
			(ticket) =>
				`<tr><td><a href="${escapeHtml(ticket.url)}">${escapeHtml(ticket.key)}</a></td><td>${escapeHtml(
					ticket.summary,
				)}</td><td>${formatTicketValue(ticket.priority ? `P${ticket.priority}` : null)}</td><td>${escapeHtml(
					ticket.status,
				)}</td><td>${formatTicketValue(ticket.targetDate)}</td></tr>`,
		)
		.join('');

	return `<h4>ENT tickets (${linkedTickets.entTickets.length})</h4><table><thead><tr><th>Key</th><th>Summary</th><th>Priority</th><th>Status</th><th>Target date</th></tr></thead><tbody>${rows}</tbody></table>`;
};

const renderJpdTicketsTable = (linkedTickets: FastshiftCsProfilerLinkedTickets) => {
	if (linkedTickets.jpdTickets.length === 0) {
		return '';
	}

	const rows = linkedTickets.jpdTickets
		.map(
			(ticket) =>
				`<tr><td><a href="${escapeHtml(ticket.url)}">${escapeHtml(ticket.key)}</a></td><td>${escapeHtml(
					ticket.summary,
				)}</td><td>${escapeHtml(ticket.status)}</td><td>${formatTicketValue(ticket.updated)}</td></tr>`,
		)
		.join('');

	return `<h4>JPD tickets (${linkedTickets.jpdTickets.length})</h4><table><thead><tr><th>Key</th><th>Summary</th><th>Status</th><th>Updated</th></tr></thead><tbody>${rows}</tbody></table>`;
};

const renderLinkedTickets = (
	theme: FastshiftCsProfilerCustomerProfileTheme,
	linkedTicketsByTheme: LinkedTicketsByTheme,
) => {
	const linkedTickets = linkedTicketsByTheme[getThemeName(theme)];

	if (!linkedTickets) {
		return '';
	}

	return `${renderEntTicketsTable(linkedTickets)}${renderJpdTicketsTable(linkedTickets)}`;
};

const renderTheme = (
	profile: FastshiftCsProfilerCustomerProfile,
	theme: FastshiftCsProfilerCustomerProfileTheme,
	linkedTicketsByTheme: LinkedTicketsByTheme,
) => {
	const { description, mitigations } = theme.themeDetails;
	let html = `<h2>${escapeHtml(getThemeName(theme))}</h2>`;
	html += `<p><strong>Tier:</strong> ${escapeHtml(theme.tier)}</p>`;

	if (description && (theme.insights.length > 0 || theme.tier === 'BASE')) {
		html += `<p>${escapeHtml(description)}</p>`;
	}

	html += renderInsights(profile, theme);

	if (mitigations?.length) {
		html += '<h3>Recommendations</h3><ul>';
		for (const mitigation of mitigations) {
			html += `<li>${escapeHtml(mitigation)}</li>`;
		}
		html += '</ul>';
	}

	html += renderLinkedTickets(theme, linkedTicketsByTheme);

	return html;
};

export const buildFastshiftCsProfilerConfluenceExport = ({
	profile,
	linkedTicketsByTheme,
	generatedAt = new Date(),
}: {
	profile: FastshiftCsProfilerCustomerProfile;
	linkedTicketsByTheme: LinkedTicketsByTheme;
	generatedAt?: Date;
}) => {
	const title = `Blocker Profile - ${profile.selection.region} - ${profile.selection.industry} - ${profile.selection.segment}`;
	const blockerThemes = profile.themes.filter(({ tier }) => tier !== 'WATCH');
	const watchThemes = profile.themes.filter(({ tier }) => tier === 'WATCH');

	let html = `<h1>${escapeHtml(title)}</h1>`;
	html += `<p><em>Generated ${formatExportDateTime(
		generatedAt,
	)} via Cloud Migration Security &amp; Compliance Blocker Profiler</em></p>`;
	html += `<p>Found ${profile.totalItems} items for this customer profile (${blockerThemes.length} require attention, ${watchThemes.length} to review).</p>`;

	if (profile.themes.length === 0) {
		return { title, html: `${html}<p>No blocker themes were found for this customer profile.</p>` };
	}

	for (const theme of sortThemesBySeverity(profile.themes)) {
		html += renderTheme(profile, theme, linkedTicketsByTheme);
	}

	return { title, html };
};

const getPlainTextFromHtml = (html: string) =>
	html.replace(/<[^>]+>/g, '').replace(/\n{3,}/g, '\n\n');

const copyHtmlWithLegacyCommand = (html: string) => {
	if (typeof document.execCommand !== 'function') {
		return false;
	}

	const container = document.createElement('div');
	container.innerHTML = html;
	container.style.position = 'fixed';
	container.style.pointerEvents = 'none';
	container.style.opacity = '0';
	document.body.appendChild(container);

	const selection = window.getSelection();
	const range = document.createRange();
	range.selectNodeContents(container);
	selection?.removeAllRanges();
	selection?.addRange(range);

	try {
		return document.execCommand('copy');
	} catch {
		return false;
	} finally {
		selection?.removeAllRanges();
		container.remove();
	}
};

export const copyFastshiftCsProfilerConfluenceExportToClipboard = async (
	html: string | Promise<string>,
) => {
	const htmlPromise = Promise.resolve(html);
	const plainTextPromise = htmlPromise.then(getPlainTextFromHtml);

	try {
		if (window.ClipboardItem && navigator.clipboard?.write) {
			await navigator.clipboard.write([
				new window.ClipboardItem({
					'text/html': htmlPromise.then(
						(resolvedHtml) => new Blob([resolvedHtml], { type: 'text/html' }),
					),
					'text/plain': plainTextPromise.then(
						(plainText) => new Blob([plainText], { type: 'text/plain' }),
					),
				}),
			]);
			return true;
		}
	} catch {
		// Fall through to plain text or legacy copy fallback.
	}

	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(await plainTextPromise);
			return true;
		}
	} catch {
		// Fall through to the legacy selection copy fallback.
	}

	return copyHtmlWithLegacyCommand(await htmlPromise);
};

export const getFastshiftCsProfilerConfluenceCreatePageUrl = (title: string) =>
	`${EXPORT_CONFLUENCE_SITE}/wiki/pages/createpage.action?spaceKey=${encodeURIComponent(
		EXPORT_CONFLUENCE_SPACE_KEY,
	)}&title=${encodeURIComponent(title)}`;
